import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ShareOutlinedIcon from '@material-ui/icons/ShareOutlined';
import { PageShell } from '../components/PageShell';
import { DeployDropzone } from '../components/DeployDropzone';
import { ApplicationsTable } from '../components/ApplicationsTable';
import { colors, cardStyle } from '../theme';
import { usePlatformPermissions } from '../usePlatformPermissions';
import type { ApplicationSummary } from '../types';
import { listApplications } from '../api';

const STEPS = [
  { icon: CloudUploadOutlinedIcon, title: 'Upload your app', text: 'Select your project folder or ZIP file, or connect your GitHub repository.' },
  { icon: DescriptionOutlinedIcon, title: 'We set it up', text: "We work out what your app is built with, set everything up and check that it works." },
  { icon: ShareOutlinedIcon, title: 'Share your link', text: "Once ready, you'll get a secure link to share with your team." },
];

export function HomePage() {
  const { canDelete } = usePlatformPermissions();
  const identityApi = useApi(identityApiRef);
  const [applications, setApplications] = useState<ApplicationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    listApplications(identityApi)
      .then(setApplications)
      .catch((err) => setError((err as Error).message));

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell>
      <div style={{ maxWidth: 1320, margin: '0 auto' }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: colors.text, margin: 0 }}>Put your app online</h1>
        <p style={{ fontSize: 15, color: colors.textMuted, marginTop: 6, marginBottom: 24 }}>
          Upload your project. We&apos;ll take care of the setup.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(280px, 1fr)', gap: 24, alignItems: 'start' }}>
          <DeployDropzone onDeployed={() => void refresh()} />

          <aside style={{ ...cardStyle, padding: 24, background: '#f8f8fc' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: colors.text, margin: '0 0 20px' }}>How it works</h2>
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} style={{ display: 'flex', gap: 14, marginBottom: 22 }}>
                <div aria-hidden="true" style={{ width: 28, height: 28, borderRadius: '50%', background: colors.primaryLight, color: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon style={{ color: colors.primary }} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: colors.text, fontSize: 15 }}>{title}</div>
                  <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 2 }}>{text}</div>
                </div>
              </div>
            ))}
          </aside>
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 40 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: colors.text, margin: 0 }}>Your apps</h2>
            <p style={{ fontSize: 14, color: colors.textMuted, margin: '4px 0 12px' }}>Apps you&apos;ve published to Mumzworld.</p>
          </div>
          <Link to="/applications" style={{ fontSize: 13, color: colors.primary, textDecoration: 'none' }}>See all</Link>
        </div>
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          {error ? (
            <div role="alert" style={{ padding: 24, color: colors.danger, fontSize: 14 }}>Couldn&apos;t load your apps: {error}</div>
          ) : applications === null ? (
            <div style={{ padding: 24, color: colors.textMuted, fontSize: 14 }}>Loading your apps…</div>
          ) : (
            <ApplicationsTable
              applications={applications.slice(0, 5)}
              canDelete={canDelete}
              onDeleted={(deleted) => setApplications((cur) => (cur ?? []).filter((a) => a.id !== deleted.id))}
              onRedeployed={() => void refresh()}
            />
          )}
        </div>
      </div>
    </PageShell>
  );
}
